import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAivyContext, useLanguageContext } from '../../context'
import { createDimensionField } from '../../helpers/culture-ranking'
import { MovedFromChip } from './dreamteam-ranking'
import HerosJourneyMatrix from './heros-journey-matrix'

const SHORT_AXES = [
  'HEROS_JOURNEY_score_coopcomp',
  'HEROS_JOURNEY_score_orderchaos'
]

// Read-only, deshalb bewusst nicht DreamteamRanking: die Komponente bringt
// ihren eigenen DndProvider mit, und zwei HTML5-Backends gleichzeitig werfen.
const TOP_N = 3

const Chip = ({ text }) => (
  <span className='inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20'>
    {text}
  </span>
)

Chip.propTypes = {
  text: PropTypes.string.isRequired
}

export const ShortProfilePreview = ({ values }) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const {
    system: { dimensions_pdf_reports }
  } = useAivyContext()

  const poles = SHORT_AXES.map((key) => {
    const { scaleEndpoints } = dimensions_pdf_reports[key]
    // >= 50 statt === 100: der Editor schreibt nur 0/100, importierte
    // Altdaten koennen aber Zwischenwerte enthalten.
    const pole = values[key] >= 50 ? scaleEndpoints.right : scaleEndpoints.left

    return { key, title: pole.title[language] }
  })

  // getPointPosition() in heros-journey-matrix liefert nur fuer exakt 0/100
  // eine Position - sonst parkt der Indikator in der linken oberen Ecke.
  const canRenderMatrix = SHORT_AXES.every((key) =>
    [0, 100].includes(values[key])
  )

  return (
    <div>
      <h3 className='sr-only'>
        {t('create_career.cultural_fit.preview.title')}
      </h3>
      {canRenderMatrix && (
        <div className='flex justify-center'>
          <HerosJourneyMatrix
            className='mx-2 mb-7 mt-6 h-28 w-64 sm:h-36 sm:w-80'
            labelClassName='text-xs'
            culturalFitValues={values}
          />
        </div>
      )}
      <div className='flex flex-wrap gap-2'>
        {poles.map(({ key, title }) => (
          <Chip key={key} text={title} />
        ))}
      </div>
    </div>
  )
}

ShortProfilePreview.propTypes = {
  values: PropTypes.object.isRequired
}

export const LongProfilePreview = ({
  items,
  dimensions,
  movedFromRanks = {}
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const getDimensionField = createDimensionField({ dimensions, t })
  const visible = expanded ? items : items.slice(0, TOP_N)

  return (
    <div>
      <h3 className='sr-only'>
        {t('create_career.cultural_fit.preview.title')}
      </h3>
      <ol className='space-y-2'>
        {visible.map((key, index) => (
          // flex-wrap, damit der Marker-Chip auf schmalen Viewports auf eine
          // eigene Zeile rutscht statt den truncate-Titel abzuschneiden
          <li key={key} className='flex flex-wrap items-center gap-2'>
            <span
              aria-hidden='true'
              className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-blue-600 text-xs font-semibold text-blue-700'
            >
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className='truncate text-xs font-semibold uppercase tracking-wide text-gray-900'>
              {getDimensionField(key, 'title')}
            </span>
            {typeof movedFromRanks[key] === 'number' && (
              <MovedFromChip rank={index + 1} fromRank={movedFromRanks[key]} />
            )}
          </li>
        ))}
      </ol>
      {items.length > TOP_N && (
        <button
          type='button'
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className='mt-3 flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800'
        >
          {expanded ? (
            <ChevronUpIcon aria-hidden='true' className='h-4 w-4' />
          ) : (
            <ChevronDownIcon aria-hidden='true' className='h-4 w-4' />
          )}
          {expanded
            ? t('create_career.cultural_fit.preview.show_less')
            : t('create_career.cultural_fit.preview.show_all', {
                count: items.length
              })}
        </button>
      )}
    </div>
  )
}

LongProfilePreview.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  dimensions: PropTypes.object,
  movedFromRanks: PropTypes.object
}
